import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
  Autocomplete,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { HtmlTooltip } from "components/HeatMap";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";

const DialectNewEdit = () => {
  usePageTitle("Edit Dialect");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
    openDialog,
    closeDialog,
  } = useStateContext();

  // Three modes:
  // 1. Single edit: dialectId is set (edit one dialect)
  // 2. Language edit: languageId + dialects array is set (edit all dialects for a language)
  // 3. Create: nothing set (create new dialect(s))
  const isSingleEditMode = !!overlayData?.dialectId;
  const isLanguageEditMode = !!overlayData?.languageId && Array.isArray(overlayData?.dialects);
  const allDialects = useMemo(() => overlayData?.allDialects || [], [overlayData?.allDialects]);
  const allLanguages = useMemo(() => overlayData?.allLanguages || [], [overlayData?.allLanguages]);

  // Edit lock for single edit mode only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "dialect",
    isSingleEditMode ? overlayData?.dialectId : null,
  );

  // Initialize names from dialects array (language edit) or single dialect name (single edit) or empty (create)
  const [names, setNames] = useState(() => {
    if (isLanguageEditMode && overlayData.dialects.length > 0) {
      return overlayData.dialects.map((d) => d.Name);
    }
    const initial = overlayData?.dialectName || "";
    return initial ? [initial] : [""];
  });
  const [initialNames] = useState(() => {
    if (isLanguageEditMode && overlayData.dialects.length > 0) {
      return overlayData.dialects.map((d) => d.Name);
    }
    const initial = overlayData?.dialectName || "";
    return initial ? [initial] : [""];
  });
  const [initialDialectIds] = useState(() => {
    if (isLanguageEditMode) {
      return overlayData.dialects.map((d) => d.id);
    }
    return isSingleEditMode ? [overlayData?.dialectId] : [];
  });

  const [languageId, setLanguageId] = useState(overlayData?.languageId || null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [loadingDeleteCheck, setLoadingDeleteCheck] = useState(false);

  const closeOverlay = () => setOverlayName("");

  const fetchDeleteCheck = useCallback(async () => {
    if (!isSingleEditMode) return;

    setLoadingDeleteCheck(true);
    try {
      const response = await api.get(
        `/linguists/dialects/${overlayData.dialectId}/delete-check`,
      );
      setDeleteCheck(response.data);
    } catch (err) {
      console.error("Error fetching delete check:", err);
    } finally {
      setLoadingDeleteCheck(false);
    }
  }, [isSingleEditMode, overlayData?.dialectId]);

  useEffect(() => {
    if (isSingleEditMode) {
      fetchDeleteCheck();
    }
  }, [isSingleEditMode, overlayData?.dialectId, fetchDeleteCheck]);

  // Check for duplicates: both within the form and against saved dialects
  const duplicateNames = useMemo(() => {
    const dupes = new Set();
    const seen = new Set();

    names.forEach((name) => {
      const trimmed = name.trim().toLowerCase();
      if (!trimmed) return;

      // Check for duplicate within the current form fields
      if (seen.has(trimmed)) {
        dupes.add(trimmed);
      }
      seen.add(trimmed);

      // Check against already-saved dialects outside this form
      if (isLanguageEditMode) {
        if (
          allDialects.some(
            (dialect) =>
              dialect.Name.toLowerCase() === trimmed &&
              !initialDialectIds.includes(dialect.id),
          )
        ) {
          dupes.add(trimmed);
        }
      } else {
        if (
          allDialects.some(
            (dialect) =>
              dialect.Name.toLowerCase() === trimmed &&
              dialect.id !== overlayData?.dialectId,
          )
        ) {
          dupes.add(trimmed);
        }
      }
    });
    return dupes;
  }, [names, allDialects, overlayData?.dialectId, isLanguageEditMode, initialDialectIds]);

  const hasDuplicate = duplicateNames.size > 0;

  const selectedLanguage = useMemo(() => {
    if (!languageId) return null;
    const found = allLanguages.find(
      (lang) => String(lang.id) === String(languageId),
    );
    if (found) return found;
    // Fallback: get language name from the dialects data itself
    if (isLanguageEditMode && overlayData?.dialects?.length > 0) {
      const dialectWithLang = overlayData.dialects.find(
        (d) => d.Language?.Name,
      );
      if (dialectWithLang?.Language) {
        return {
          id: languageId,
          Name: dialectWithLang.Language.Name,
        };
      }
    }
    return null;
  }, [languageId, allLanguages, isLanguageEditMode, overlayData?.dialects]);

  const handleSave = async () => {
    const validNames = names.map((n) => n.trim()).filter(Boolean);

    if (validNames.length === 0) {
      showSnackbar("At least one dialect name is required", theme, "error");
      return;
    }

    if (!languageId) {
      showSnackbar("Please select a language", theme, "error");
      return;
    }

    if (hasDuplicate) {
      showSnackbar("One or more dialect names already exist", theme, "error");
      return;
    }

    setSaving(true);

    try {
      if (isLanguageEditMode) {
        // Language edit mode: sync all dialects for this language
        const currentNames = validNames;
        const originalNames = initialNames.map((n) => n.trim()).filter(Boolean);

        // Find dialects to create (new names not in original)
        const toCreate = currentNames.filter(
          (name) => !originalNames.includes(name),
        );

        // Find dialects to delete (original names not in current)
        const toDeleteIndices = originalNames
          .map((name, idx) => (!currentNames.includes(name) ? idx : -1))
          .filter((idx) => idx !== -1);
        const toDeleteIds = toDeleteIndices
          .map((idx) => initialDialectIds[idx])
          .filter(Boolean);

        // Execute deletes first
        for (const deleteId of toDeleteIds) {
          await api.delete(`/linguists/dialects/${deleteId}`);
        }

        // Execute creates
        for (const dialectName of toCreate) {
          await api.post("/linguists/dialects", {
            Name: dialectName,
            LanguageID: languageId,
          });
        }

        const changedCount = toCreate.length + toDeleteIds.length;
        if (changedCount > 0) {
          showSnackbar(
            `Dialects updated successfully (${toCreate.length} added, ${toDeleteIds.length} removed)`,
            theme,
            "success",
          );
        } else {
          showSnackbar("No changes to save", theme, "info");
          setSaving(false);
          return;
        }

        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "Dialect",
            recordId: languageId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else if (isSingleEditMode) {
        // Single dialect edit mode
        await api.put(`/linguists/dialects/${overlayData.dialectId}`, {
          Name: validNames[0],
          LanguageID: languageId,
        });
        showSnackbar("Dialect updated successfully", theme, "success");

        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "Dialect",
            recordId: overlayData.dialectId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        // Create mode: create new dialects
        const createdIds = [];
        for (const dialectName of validNames) {
          const response = await api.post("/linguists/dialects", {
            Name: dialectName,
            LanguageID: languageId,
          });
          createdIds.push(response.data.id);
        }
        showSnackbar(
          `${validNames.length} dialect${validNames.length > 1 ? "s" : ""} created successfully`,
          theme,
          "success",
        );

        if (socket && createdIds.length > 0) {
          socket.emit("settings:saved", {
            settingsType: "Dialect",
            recordId: createdIds[createdIds.length - 1],
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      overlayData?.onDialectsSaved?.();
      setSaving(false);
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving dialect",
        theme,
      );
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isSingleEditMode) return;

    openDialog({
      theme: theme,
      title: "Delete Dialect",
      message: `Are you sure you want to delete "${names[0] || "this dialect"}"? This action cannot be undone.`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        setDeleting(true);

        try {
          await api.delete(`/linguists/dialects/${overlayData.dialectId}`);
          showSnackbar("Dialect deleted successfully", theme, "success");

          if (socket) {
            socket.emit("settings:saved", {
              settingsType: "Dialect",
              recordId: overlayData.dialectId,
              changeAction: "Deleted",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          overlayData?.onDialectsSaved?.();
          closeDialog();
          closeOverlay();
        } catch (err) {
          console.error(err);
          closeDialog();
          handleErrorDialog(
            err.response?.data?.error || "Error deleting dialect",
            theme,
          );
        } finally {
          setDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const handleDeleteClick = () => {
    if (!deleteCheck?.canDelete) {
      const reasons = [];
      if (deleteCheck?.linguistCount > 0) {
        reasons.push(`Linguist${deleteCheck.linguistCount > 1 ? "s" : ""}`);
      }
      if (deleteCheck?.serviceCount > 0) {
        reasons.push(`Service${deleteCheck.serviceCount > 1 ? "s" : ""}`);
      }
      showSnackbar(
        `Can't delete dialect being used by ${reasons.join(" and ")}`,
        theme,
        "error",
      );
      return;
    }
    handleDelete();
  };

  const renderDeleteTooltip = () => {
    if (!deleteCheck || deleteCheck.canDelete) return null;

    return (
      <Box>
        {deleteCheck.linguistCount > 0 && (
          <Box mb={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Linguists: {deleteCheck.linguistCount}
            </Typography>
            {deleteCheck.linguists.map((linguist) => (
              <Typography key={linguist.id} variant="body2">
                {linguist.name}
              </Typography>
            ))}
          </Box>
        )}
        {deleteCheck.serviceCount > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              Services: {deleteCheck.serviceCount}
            </Typography>
            {deleteCheck.services.map((service) => (
              <Typography key={service.id} variant="body2">
                #{service.projectNumber}
              </Typography>
            ))}
            {deleteCheck.serviceCount > 10 && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontStyle="italic"
              >
                ...and {deleteCheck.serviceCount - 10} more
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Determine page title
  let pageTitle = "New Dialect";
  if (isSingleEditMode) {
    pageTitle = "Edit Dialect";
  } else if (isLanguageEditMode) {
    pageTitle = `Edit ${selectedLanguage?.Name || "Language"} Dialects`;
  }

  // Whether language selector should be disabled
  const isLanguageLocked = isLanguageEditMode;

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isSingleEditMode && (
          <HtmlTooltip
            title={deleteCheck?.canDelete ? "" : renderDeleteTooltip()}
            arrow
            placement="bottom"
          >
            <span>
              {isMobile ? (
                <IconButton
                  color="error"
                  onClick={handleDeleteClick}
                  disabled={deleting || loadingDeleteCheck}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.error.main}`,
                    borderRadius: "12px",
                    ...(!deleteCheck?.canDelete && {
                      opacity: 0.5,
                      borderColor: theme.palette.action.disabled,
                    }),
                  }}
                >
                  <DeleteRoundedIcon />
                </IconButton>
              ) : (
                <Button
                  loading={deleting}
                  color="error"
                  variant="outlined"
                  size="large"
                  endIcon={<DeleteRoundedIcon />}
                  onClick={handleDeleteClick}
                  disabled={deleting || loadingDeleteCheck}
                  sx={{
                    borderRadius: "12px",
                    ...(!deleteCheck?.canDelete && {
                      opacity: 0.5,
                      color: theme.palette.action.disabled,
                      borderColor: theme.palette.action.disabled,
                    }),
                  }}
                >
                  Delete
                </Button>
              )}
            </span>
          </HtmlTooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        <Tooltip
          title={
            isMobile && (saving || hasDuplicate)
              ? "Please fix errors before saving"
              : ""
          }
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving || hasDuplicate}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                loading={saving}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSave}
                disabled={saving || hasDuplicate}
                sx={{ borderRadius: "12px" }}
              >
                Save
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Overlay
        id="dialect-new-edit"
        open={true}
        onClose={closeOverlay}
        title={pageTitle}
        height="fit-content"
        footer={footerActions}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Dialect Details
              </Typography>
            </Grid>

            {hasDuplicate && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                  One or more dialect names already exist in the system.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              {isLanguageLocked ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Language"
                  value={selectedLanguage?.Name || ""}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />
              ) : (
                <Autocomplete
                  fullWidth
                  size="small"
                  options={allLanguages}
                  getOptionLabel={(option) => option.Name || ""}
                  value={selectedLanguage}
                  onChange={(_, newValue) => setLanguageId(newValue?.id || null)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Language *"
                      placeholder="Select a language"
                    />
                  )}
                />
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography variant="subtitle2">Dialect Names</Typography>
                <IconButton
                  size="small"
                  onClick={() => setNames((prev) => [...prev, ""])}
                  sx={{ p: 0.5 }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
                <Divider sx={{ flex: 1, ml: 0.5 }} />
              </Box>

              {names.map((dialectName, index) => {
                const trimmed = dialectName.trim().toLowerCase();
                const isDupe = trimmed && duplicateNames.has(trimmed);
                return (
                  <Box
                    key={`dialect-name-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={index === 0 ? "Dialect Name *" : `Dialect Name ${index + 1}`}
                      value={dialectName}
                      onChange={(e) => {
                        setNames((prev) => {
                          const next = [...prev];
                          next[index] = e.target.value;
                          return next;
                        });
                      }}
                      inputProps={{ maxLength: 100 }}
                      placeholder="e.g., Castilian Spanish, Mandarin, Quebec French"
                      error={isDupe}
                    />
                    {names.length > 1 && (
                      <Box sx={{ flex: "0 0 auto", mt: { xs: 0, md: 0.5 } }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setNames((prev) => prev.filter((_, i) => i !== index));
                          }}
                        >
                          <RemoveRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ mt: 0.5 }} />
            </Grid>
          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default DialectNewEdit;
